/**
 * NotFound — 404 page.
 *
 * Empty fish-tank SVG with one lonely fish + a "Back to dashboard" button
 * that navigates to `/`. Wired via the catch-all `*` route in App.tsx.
 */
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';

// ── Illustration ──────────────────────────────────────────────────────────────

function EmptyTankIllustration() {
  return (
    <svg
      width="180"
      height="130"
      viewBox="0 0 180 130"
      fill="none"
      aria-hidden="true"
    >
      {/* Tank frame */}
      <rect
        x="10"
        y="16"
        width="160"
        height="102"
        rx="6"
        stroke="var(--surface-stroke)"
        strokeWidth="2"
      />

      {/* Water body */}
      <rect
        x="11"
        y="56"
        width="158"
        height="61"
        rx="4"
        fill="var(--accent-soft)"
        opacity="0.18"
      />

      {/* Water surface ripple */}
      <path
        d="M11 56 Q45 51 90 56 Q135 61 169 56"
        stroke="var(--accent-cyan)"
        strokeWidth="1.5"
        fill="none"
        opacity="0.45"
      />

      {/* Sandy bottom */}
      <rect
        x="11"
        y="109"
        width="158"
        height="9"
        rx="4"
        fill="var(--surface-glass)"
        opacity="0.6"
      />

      {/* Lone fish — body */}
      <ellipse
        cx="90"
        cy="84"
        rx="18"
        ry="10"
        fill="var(--accent-cyan)"
        opacity="0.65"
      />

      {/* Fish tail */}
      <path
        d="M108 84 L122 74 L122 94 Z"
        fill="var(--accent-cyan)"
        opacity="0.45"
      />

      {/* Fish eye */}
      <circle cx="78" cy="82" r="2.5" fill="var(--bg-base)" />
      <circle cx="78" cy="82" r="1" fill="var(--text-3)" />

      {/* Decorative bubbles */}
      <circle
        cx="68"
        cy="70"
        r="2.5"
        stroke="var(--accent-cyan)"
        strokeWidth="1"
        opacity="0.35"
      />
      <circle
        cx="74"
        cy="62"
        r="1.75"
        stroke="var(--accent-cyan)"
        strokeWidth="1"
        opacity="0.25"
      />

      {/* 404 ghost text inside tank */}
      <text
        x="90"
        y="42"
        textAnchor="middle"
        fontSize="16"
        fontWeight="700"
        fill="var(--text-3)"
        fontFamily="ui-monospace, monospace"
        opacity="0.4"
      >
        404
      </text>
    </svg>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function NotFound() {
  const navigate = useNavigate();

  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen gap-6 px-6 text-center"
      data-testid="not-found-page"
    >
      <div className="opacity-90">
        <EmptyTankIllustration />
      </div>

      <div className="flex flex-col gap-2">
        <h1 className="text-lg font-semibold text-text-1">
          This reef zone doesn&apos;t exist
        </h1>
        <p className="text-sm text-text-3 max-w-xs">
          The page you&apos;re looking for has swum away.
        </p>
      </div>

      <Button variant="primary" size="sm" onClick={() => navigate('/')}>
        Back to dashboard
      </Button>
    </div>
  );
}
